import { create } from 'zustand';
import { GalleryItem } from '../types/gallery';
import { Brandbook } from '../types/brandbook';
import aiService from '../services/aiService';

interface GalleryState {
  items: GalleryItem[];
  brandbooks: Brandbook[];
  isLoading: boolean;
  // Se a galeria ja foi buscada ao menos uma vez nesta sessao. Desacopla
  // "carregada" de "tem itens": um addItem otimista deixa items.length > 0 sem
  // que o historico tenha sido buscado (caso do GallerySourceNode).
  hasFetchedGallery: boolean;

  // Actions
  fetchGallery: () => Promise<void>;
  fetchBrandbooks: () => Promise<void>;
  addItem: (item: GalleryItem) => void;
  updateItem: (id: string, updates: Partial<GalleryItem>) => void;
  addBrandbook: (brandbook: Brandbook) => void;
  setItems: (items: GalleryItem[]) => void;
}

export const useGalleryStore = create<GalleryState>((set, get) => ({
  items: [],
  brandbooks: [],
  isLoading: false,
  hasFetchedGallery: false,

  fetchGallery: async () => {
    // Dedupe: varios GallerySourceNode podem montar juntos e disparar o fetch.
    if (get().isLoading) return;
    set({ isLoading: true });
    try {
      const images = await aiService.getUserImages();
      const galleryItems: GalleryItem[] = images.map((img: any) => ({
        id: `img_${img.id}`,
        backendId: img.id, // Store backend ID to avoid CORS on re-upload
        type: 'image' as const,
        src: img.url, // Adapt based on backend response
        prompt: img.metadata?.prompt || img.prompt || 'Imagem gerada',
        createdAt: img.createdAt || img.created_at,
        campaignId: img.campaignId || img.campaign_id ? Number(img.campaignId || img.campaign_id) : undefined,
        channel: img.channel || null,
        tags: img.tags || [],
      }));
      set({ items: galleryItems, hasFetchedGallery: true });
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchBrandbooks: async () => {
    try {
      const response = await aiService.getBrandbooks();
      set({ brandbooks: response });
    } catch (error) {
      console.error('Failed to fetch brandbooks:', error);
    }
  },

  addItem: (item) => set((state) => ({ items: [item, ...state.items] })),

  updateItem: (id, updates) => set((state) => ({
    items: state.items.map((item) => {
      if (item.id !== id) return item;
      if (item.type === 'image') {
        return { ...item, ...updates } as GalleryItem;
      }
      return item;
    }),
  })),

  addBrandbook: (brandbook) => set((state) => {
    if (state.brandbooks.some((b) => b.id === brandbook.id)) return state;
    return { brandbooks: [brandbook, ...state.brandbooks] };
  }),

  setItems: (items) => set({ items }),
}));
